// On-demand coach trigger button for the REVIEW tab.
//
// Fires POST /api/coach with trigger='ad-hoc'. Opus is slow (~30-60s),
// so the button shows a long-running loading state. On success, the
// mutation invalidates ['coach-latest'] so the REVIEW tab refetches
// and renders the new ad-hoc call's suggestions (server now picks
// 'latest ok of any trigger' by default — see U3 follow-up).

import { useState } from 'react';

import {
  C,
  FONT_MONO,
  LABEL_TRACKING,
} from '../../lib/design';
import { useRunCoachMutation } from '../../lib/coach-actions';
import { formatApiError } from '../../lib/api';
import { dataDependency, useDataHealth } from '../../lib/data-health';

const PRESETS = [2, 4, 8, 12] as const;
type Preset = (typeof PRESETS)[number];

export function RunCoachButton() {
  const run = useRunCoachMutation();
  const [weeks, setWeeks] = useState<Preset>(2);
  const health = useDataHealth();
  const healthUnavailable = health.isError;
  const healthChecking = !health.data && health.isLoading;
  const paused = !healthUnavailable && health.data
    ? health.data.dependencies.filter((d) => !d.ready)
    : [];

  return (
    <div
      style={{
        padding: 14,
        background: C.panel,
        border: `1px dashed ${C.line}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          color: C.dim,
          fontSize: 10,
          letterSpacing: LABEL_TRACKING,
          fontFamily: FONT_MONO,
        }}
      >
        ASK THE COACH
      </div>
      {(healthUnavailable || healthChecking) && (
        <div style={{ color: C.warn, fontSize: 10, lineHeight: 1.5, fontFamily: FONT_MONO }}>
          DATA HEALTH · {healthUnavailable ? 'UNREACHABLE. THE COACH WILL REVALIDATE SOURCES SERVER-SIDE.' : 'CHECKING…'}
        </div>
      )}
      {!healthUnavailable && !healthChecking && paused.length > 0 && (
        <div style={{ color: C.warn, fontSize: 10, lineHeight: 1.5, fontFamily: FONT_MONO }}>
          DEPENDENCY GATES · {paused.map((d) => d.key.replace('_', ' ')).join(' · ')}. The coach will still run, but recommendations that depend on these signals will be paused.
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {PRESETS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWeeks(w)}
              disabled={run.isPending}
              style={{
                background: weeks === w ? C.signal : 'transparent',
                color: weeks === w ? C.bg : C.dim,
                border: `1px solid ${weeks === w ? C.signal : C.line}`,
                borderRadius: 0,
                padding: '4px 10px',
                fontSize: 10,
                letterSpacing: LABEL_TRACKING,
                fontWeight: 700,
                fontFamily: FONT_MONO,
                cursor: run.isPending ? 'not-allowed' : 'pointer',
                opacity: run.isPending ? 0.5 : 1,
              }}
            >
              {w}W
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => run.mutate({ weeks })}
          disabled={run.isPending}
          style={{
            background: C.signal,
            color: C.bg,
            border: `1px solid ${C.signal}`,
            borderRadius: 0,
            padding: '8px 18px',
            fontSize: 11,
            letterSpacing: LABEL_TRACKING,
            fontWeight: 700,
            fontFamily: FONT_MONO,
            cursor: run.isPending ? 'wait' : 'pointer',
            opacity: run.isPending ? 0.6 : 1,
          }}
        >
          {run.isPending ? 'RUNNING COACH (~30–60s)…' : 'RUN COACH NOW'}
        </button>
      </div>
      {run.data && run.data.warnings.length > 0 && (
        <div style={{ color: C.warn, fontSize: 10, lineHeight: 1.5, fontFamily: FONT_MONO }}>
          ACCEPTED WITH {run.data.warnings.length} RAIL WARNING{run.data.warnings.length === 1 ? '' : 'S'}
          — THE CALL'S WARNING PANEL BELOW LISTS WHAT'S UNVERIFIED.
        </div>
      )}
      {run.error && (
        <div
          style={{
            color: C.red,
            fontSize: 11,
            fontFamily: FONT_MONO,
          }}
        >
          {formatApiError(run.error)}
        </div>
      )}
    </div>
  );
}
